export const extractColorsFromImageUrl = async (
  imageUrl: string,
  setColor: (colors: string[]) => void,
) => {
  const img = new Image()
  img.crossOrigin = "Anonymous" // Enable CORS for cross-origin images
  img.src = imageUrl

  img.onload = () => {
    const tempCanvas = document.createElement("canvas")
    const ctx = tempCanvas.getContext("2d")

    if (ctx) {
      tempCanvas.width = img.width
      tempCanvas.height = img.height
      ctx.drawImage(img, 0, 0)

      const imageData = ctx.getImageData(0, 0, img.width, img.height)
      const pixels = imageData.data

      const colorMap: Record<string, number> = {}

      // Populate colorMap with pixel color frequencies
      for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i]
        const g = pixels[i + 1]
        const b = pixels[i + 2]
        const alpha = pixels[i + 3]

        if (alpha === 0) continue // Skip transparent pixels

        const rgb = `rgb(${r},${g},${b})`
        colorMap[rgb] = (colorMap[rgb] || 0) + 1
      }

      // Sort colors by frequency
      let sortedColors = Object.entries(colorMap)
        .sort(([, countA], [, countB]) => countB - countA)
        .map(([color]) => color)

      // Helper to calculate luminance
      const getLuminance = (r: number, g: number, b: number): number => {
        const normalize = (value: number) =>
          value <= 0.03928
            ? value / 12.92
            : Math.pow((value + 0.055) / 1.055, 2.4)
        return (
          0.2126 * normalize(r / 255) +
          0.7152 * normalize(g / 255) +
          0.0722 * normalize(b / 255)
        )
      }

      // Helper to calculate contrast ratio
      const getContrastRatio = (
        luminance1: number,
        luminance2: number,
      ): number => {
        const light = Math.max(luminance1, luminance2)
        const dark = Math.min(luminance1, luminance2)
        return (light + 0.05) / (dark + 0.05)
      }

      // Filter colors based on contrast
      const baseLuminance = getLuminance(255, 255, 255) // White
      sortedColors = sortedColors.filter((color) => {
        const match = color.match(/\d+/g)
        if (!match) return false
        const [r, g, b] = match.map(Number)
        const luminance = getLuminance(r, g, b)
        return getContrastRatio(luminance, baseLuminance) >= 2.7 // Adjust threshold as needed
      })

      // Helper to calculate Euclidean distance between two colors
      const colorDistance = (color1: string, color2: string): number => {
        const [r1, g1, b1] = color1.match(/\d+/g)!.map(Number)
        const [r2, g2, b2] = color2.match(/\d+/g)!.map(Number)
        return Math.sqrt(
          Math.pow(r1 - r2, 2) + Math.pow(g1 - g2, 2) + Math.pow(b1 - b2, 2),
        )
      }

      // Remove similar colors using colorDistance
      const removeSimilarColors = (
        colors: string[],
        threshold: number,
      ): string[] => {
        const uniqueColors: string[] = []
        for (const color of colors) {
          const isSimilar = uniqueColors.some(
            (uniqueColor) => colorDistance(uniqueColor, color) < threshold,
          )
          if (!isSimilar) {
            uniqueColors.push(color)
          }
        }
        return uniqueColors
      }

      // Apply removeSimilarColors to filter sortedColors
      const uniqueColors = removeSimilarColors(sortedColors, 100)

      // Set the top 8 unique colors
      setColor(uniqueColors.slice(0, 8))
    }
  }

  img.onerror = () => {
    console.error("Failed to load the image from the URL.")
  }
}
